import React, { useMemo, useState } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, Modal, ScrollView } from 'react-native';
import { SVG_COMPONENTS } from '@/assets/admin';
import { IMessageResident } from '@/types/admin/messages';
import { useAdminColors } from '@/hooks/admin/useAdminColors';
import { GlassCard } from '@/components/shared/redesign';

interface UnassignedRecipientsNoticeProps {
  recipients: IMessageResident[];
}

export const UnassignedRecipientsNotice: React.FC<UnassignedRecipientsNoticeProps> = ({ recipients }) => {
  const { redesign } = useAdminColors();
  const [open, setOpen] = useState(false);
  const unassigned = useMemo(() => {
    return (recipients || []).filter((r: any) => !(r?.leaseCustomers?.[0]?.account && Object.keys(r.leaseCustomers?.[0]?.account || {}).length > 0));
  }, [recipients]);

  if (!unassigned.length) return null;

  return (
    <View style={styles.container}>
      <View
        style={[
          styles.banner,
          {
            backgroundColor: redesign.accent.orangeLight,
            borderColor: redesign.accent.orange,
          },
        ]}
      >
        <TouchableOpacity style={styles.headerRow} onPress={() => setOpen(true)} activeOpacity={0.8}>
          <Text style={[styles.title, { color: redesign.text.primary }]}>
            Unassigned recipients
          </Text>
          <View style={styles.countPill}>
            <Text style={[styles.countText, { color: redesign.text.muted }]}>
              {unassigned.length}
            </Text>
            <Text style={[styles.chevron, { color: redesign.text.muted }]}>{'>'}</Text>
          </View>
        </TouchableOpacity>
        <Text style={[styles.subtitle, { color: redesign.text.primary }]}>
          The following residents do not have a resio account and will receive an email invitation to join the conversation.
        </Text>
      </View>

      <Modal visible={open} animationType="fade" transparent={true} onRequestClose={() => setOpen(false)}>
        <View style={styles.modalBackdrop}>
          <GlassCard
            padding={0}
            style={[styles.modalCard, { backgroundColor: redesign.bg.modal }]}
          >
            <View
              style={[
                styles.modalHeader,
                { borderBottomColor: redesign.card.border },
              ]}
            >
              <Text style={[styles.modalTitle, { color: redesign.text.primary }]}>
                Conflict details
              </Text>
              <TouchableOpacity onPress={() => setOpen(false)}>
                <SVG_COMPONENTS.X_CIRCLE width={20} height={20} color={redesign.text.muted} />
              </TouchableOpacity>
            </View>
            <ScrollView style={styles.modalBody} contentContainerStyle={styles.modalBodyContent}>
              <Text style={[styles.modalIntro, { color: redesign.text.primary }]}>
                The following residents do not have a resio account and will receive an email invitation to join the conversation.
              </Text>
              <View
                style={[
                  styles.listWrap,
                  { borderColor: redesign.card.border },
                ]}
              >
                {unassigned.map((resident: any) => {
                  const lc = resident.leaseCustomers?.[0];
                  const displayName = lc?.first_name ? `${lc.first_name} ${lc.last_name}` : lc?.email;
                  const role = lc?.customerType === 'primary' ? 'resident' : 'guarantor';
                  const unit = resident.unitNumberSpace;
                  const status = (resident.statusType || '').toString();
                  const statusFormatted = status ? `${status.charAt(0).toUpperCase()}${status.slice(1)}` : '';
                  return (
                    <View key={resident.id} style={styles.itemRow}>
                      <View
                        style={[
                          styles.simpleAvatar,
                          { backgroundColor: redesign.card.bg },
                        ]}
                      >
                        <Text style={[styles.simpleAvatarText, { color: redesign.text.primary }]}>
                          {(lc?.first_name?.[0] || lc?.email?.[0] || '?').toUpperCase()}
                        </Text>
                      </View>
                      <View style={styles.itemTextWrap}>
                        <Text style={[styles.itemTitle, { color: redesign.text.primary }]} numberOfLines={1}>
                          {displayName}
                        </Text>
                        <Text style={[styles.itemSubtitle, { color: redesign.text.muted }]} numberOfLines={1}>
                          {statusFormatted} {role} - {unit}
                        </Text>
                      </View>
                    </View>
                  );
                })}
              </View>
            </ScrollView>
          </GlassCard>
        </View>
      </Modal>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    paddingHorizontal: 16,
    paddingTop: 12,
    marginBottom: 16,
  },
  banner: {
    borderWidth: 1,
    borderRadius: 12,
    padding: 12,
  },
  headerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingBottom: 6,
  },
  title: {
    fontSize: 16,
    fontWeight: '600',
  },
  countPill: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  countText: {
    fontSize: 14,
  },
  chevron: {
    fontSize: 16,
    marginLeft: 2,
  },
  subtitle: {
    fontSize: 14,
    lineHeight: 18,
  },
  modalBackdrop: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'center',
    padding: 16,
  },
  modalCard: {
    overflow: 'hidden',
  },
  modalHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
  },
  modalTitle: {
    fontSize: 16,
    fontWeight: '700',
  },
  modalBody: {
    maxHeight: 420,
    minWidth: 320,
  },
  modalBodyContent: {
    padding: 16,
  },
  modalIntro: {
    fontSize: 14,
    marginBottom: 12,
  },
  listWrap: {
    borderWidth: 1,
    borderRadius: 8,
    padding: 12,
    gap: 12,
  },
  itemRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  itemTextWrap: {
    flex: 1,
  },
  simpleAvatar: {
    width: 28,
    height: 28,
    borderRadius: 14,
    alignItems: 'center',
    justifyContent: 'center',
  },
  simpleAvatarText: {
    fontSize: 12,
    fontWeight: '700',
  },
  itemTitle: {
    fontSize: 14,
    fontWeight: '600',
  },
  itemSubtitle: {
    fontSize: 12,
  },
});

export default UnassignedRecipientsNotice;
